import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import axios from 'axios';
import { useAdmin } from '../AdminContext';
import AdminLayout from '../AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  Search,
  Upload,
  Calendar,
  Clock,
  ArrowLeft,
  FileUp,
  FileCode,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Square,
  Image as ImageIcon,
  Check,
  X,
  Bold,
  Italic,
  Underline,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  List,
  ListOrdered,
  Link as LinkIcon,
  Sparkles,
  RefreshCw,
  ExternalLink,
  SearchCheck,
  Globe,
  ArrowUp,
  ArrowDown,
  PanelLeft,
  PanelRight,
  Library,
  Save,
  Send,
  EyeOff,
} from 'lucide-react';

import { PLACEHOLDER_IMAGES } from '../../data/placeholderImages';
import { downscaleImage, formatBytes, MAX_UPLOAD_DIMENSION } from '../utils/imageResize';
import { ARTICLE_PROSE_CLASSES, stripRepeatedTitle } from '../../lib/articleProse';

const RESERVED_SLUGS = new Set([
  'admin', 'api', 'app', 'login', 'terms', 'privacy', 'quiz', 'connect',
  'pair', 'join', 'invite', 'ig', 'fb', 'tt', 'rd', 'x', 'th', 'yt',
  'dashboard', 'reminders', 'moodsync', 'brownies', 'conflict', 'settings',
  'profile-setup', 'never-forget-anything', 'solve-fights-in-5-minutes',
  'love-with-intentionality', 'stay-connected-in-60-seconds', 'blog', 'sitemap', 'robots'
]);

const CATEGORIES = [
  'Communication',
  'Date Ideas',
  'Love Languages',
  'Conflict Resolution',
  'Relationship Tips',
  'App Updates',
];

// Import formats offered in the editor header. Both post to the same endpoint;
// the accept list keeps the file picker from offering the wrong kind.
const IMPORT_FORMATS = {
  docx: {
    label: 'Word document',
    button: 'Import .docx',
    accept: '.docx',
    extensions: ['.docx'],
  },
  md: {
    label: 'Markdown document',
    button: 'Import .md',
    accept: '.md,.markdown,.mdown,.mkd,.txt',
    extensions: ['.md', '.markdown', '.mdown', '.mkd', '.txt'],
  },
};

// Display widths offered when an image in the article is selected. Percentages
// rather than pixels so the article stays responsive on phones. The slider can
// set anything in between; these are just the common stops.
const IMAGE_SIZES = [
  { id: 'small', label: 'S', title: 'Small (33%)', width: 33 },
  { id: 'medium', label: 'M', title: 'Medium (66%)', width: 66 },
  { id: 'full', label: 'L', title: 'Full width (100%)', width: 100 },
];

const MIN_IMAGE_WIDTH = 10;

// Height is opt-in: left on auto the image keeps its own aspect ratio, which is
// what an article wants by default. Setting a height is what makes object-fit
// meaningful, so the two controls belong together.
const IMAGE_HEIGHT = { min: 80, max: 900, default: 320 };

const IMAGE_FITS = [
  { id: 'cover', label: 'Cover', title: 'Fill the box, cropping the overflow' },
  { id: 'contain', label: 'Fit', title: 'Fit the whole image inside the box' },
];

const IMAGE_RADII = [
  // Sharp corners read better as a square than as the word "None".
  { id: 'none', Icon: Square, radius: 0, title: 'Square corners' },
  { id: 'sm', label: 'S', radius: 8, title: 'Small corner radius' },
  { id: 'md', label: 'M', radius: 16, title: 'Medium corner radius' },
  { id: 'lg', label: 'L', radius: 28, title: 'Large corner radius' },
];

// The first three keep the image on its own line and position it with margins.
// The wrap options float it so the following paragraphs flow beside it; the
// image sits in its own paragraph, so the float never traps the caret inside
// running text, and the article page contains it with flow-root.
const IMAGE_ALIGNMENTS = [
  { id: 'left', title: 'Align left', Icon: AlignLeft, marginLeft: '0px', marginRight: 'auto' },
  { id: 'center', title: 'Centre', Icon: AlignCenter, marginLeft: 'auto', marginRight: 'auto' },
  { id: 'right', title: 'Align right', Icon: AlignRight, marginLeft: 'auto', marginRight: '0px' },
  {
    id: 'wrap-left',
    title: 'Image on the left, text wraps around it',
    Icon: PanelLeft,
    float: 'left',
    margin: ['0.25rem', '1.25rem', '0.75rem', '0px'],
  },
  {
    id: 'wrap-right',
    title: 'Image on the right, text wraps around it',
    Icon: PanelRight,
    float: 'right',
    margin: ['0.25rem', '0px', '0.75rem', '1.25rem'],
  },
];

// Past this a wrapped image leaves too little room for the text beside it.
const MAX_WRAPPED_WIDTH = 50;

const blankForm = () => ({
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  category: 'Relationship Tips',
  image: '',
  emoji: '💕',
  read_time: '5',
  published: false,
  meta_title: '',
  meta_description: '',
  keywords: '',
});

// Text or at least one image; an editor that was typed in and cleared still
// holds markup like <p><br></p>.
const hasArticleBody = (html) =>
  /<img\b/i.test(html || '') ||
  (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;

const EMOJIS = ['💕', '💖', '❤️', '🌹', '✨', '💑', '💌', '🎯', '🔥', '🎁', '📅', '💬'];

// WYSIWYG Rich Text Editor Component. The ref exposes insertImage(url, alt) so
// the image library in the parent can drop a picture where the caret was.
const RichTextEditor = forwardRef(({ value, onChange, onOpenLibrary }, ref) => {
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const draggedImageRef = useRef(null);
  const [dropMarker, setDropMarker] = useState(null);
  const wrapperRef = useRef(null);
  const inlineImageInputRef = useRef(null);
  const isInternalChangeRef = useRef(false);
  const [uploadingInline, setUploadingInline] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [sizeBarPos, setSizeBarPos] = useState(null);
  const [imageBox, setImageBox] = useState(null);
  const dragRef = useRef(null);
  const justDraggedRef = useRef(false);
  const { API } = useAdmin();

  // Size and alignment live in the style attribute rather than width/align
  // attributes: the article is rendered straight into the page with
  // dangerouslySetInnerHTML, so an inline style is what survives and beats the
  // prose stylesheet.
  const applyImageStyle = (img, { width, align, height, fit, radius } = {}) => {
    if (width != null) img.style.width = `${width}%`;

    // undefined means "leave height alone"; 'auto' clears it back to the
    // natural aspect ratio. Only default it when the image has none yet.
    if (height !== undefined) {
      img.style.height = height === 'auto' ? 'auto' : `${height}px`;
    } else if (!img.style.height) {
      img.style.height = 'auto';
    }

    if (fit) img.style.objectFit = fit;
    if (radius != null) img.style.borderRadius = `${radius}px`;
    img.style.display = 'block';

    if (align) {
      const preset = IMAGE_ALIGNMENTS.find((a) => a.id === align) || IMAGE_ALIGNMENTS[1];
      if (preset.float) {
        img.style.float = preset.float;
        [img.style.marginTop, img.style.marginRight, img.style.marginBottom, img.style.marginLeft] =
          preset.margin;
        if ((parseInt(img.style.width, 10) || 100) > MAX_WRAPPED_WIDTH) {
          img.style.width = `${MAX_WRAPPED_WIDTH}%`;
        }
      } else {
        // Back to a block of its own: drop the float and hand the vertical
        // spacing back to the article stylesheet.
        img.style.float = '';
        img.style.marginTop = '';
        img.style.marginBottom = '';
        img.style.marginLeft = preset.marginLeft;
        img.style.marginRight = preset.marginRight;
      }
    } else if (!img.style.marginLeft) {
      img.style.marginLeft = 'auto';
      img.style.marginRight = 'auto';
    }
  };

  // Park the size bar just under the selected image, clamped to the editor's
  // visible band so the wrapper's overflow-hidden cannot clip it. Returns null
  // when the image has been scrolled out of view, which hides the bar.
  const positionSizeBar = useCallback(() => {
    const img = selectedImage;
    const wrap = wrapperRef.current;
    const editor = editorRef.current;
    if (!img || !wrap || !editor || !img.isConnected) return setSizeBarPos(null);

    const wrapRect = wrap.getBoundingClientRect();
    const editorRect = editor.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    if (imgRect.bottom < editorRect.top || imgRect.top > editorRect.bottom) {
      setImageBox(null);
      return setSizeBarPos(null);
    }

    setImageBox({
      top: imgRect.top - wrapRect.top,
      left: imgRect.left - wrapRect.left,
      width: imgRect.width,
      height: imgRect.height,
    });

    // Below the image by default, above it when that would overflow the editor.
    // PANEL_H is the three-row control panel's approximate height.
    const PANEL_H = 116;
    const below = imgRect.bottom + 8;
    const top = below + PANEL_H > editorRect.bottom ? imgRect.top - PANEL_H - 4 : below;

    setSizeBarPos({
      top:
        Math.min(Math.max(top, editorRect.top + 4), editorRect.bottom - PANEL_H - 4) -
        wrapRect.top,
      left: Math.max(imgRect.left - wrapRect.left, 8),
    });
  }, [selectedImage]);

  useEffect(() => {
    if (!selectedImage) {
      setImageBox(null);
      return setSizeBarPos(null);
    }
    positionSizeBar();

    const editor = editorRef.current;
    editor?.addEventListener('scroll', positionSizeBar);
    window.addEventListener('resize', positionSizeBar);
    return () => {
      editor?.removeEventListener('scroll', positionSizeBar);
      window.removeEventListener('resize', positionSizeBar);
    };
  }, [selectedImage, positionSizeBar]);

  // Clicking an image selects it; clicking anything else lets it go.
  const handleEditorClick = (e) => {
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return;
    }
    setSelectedImage(e.target.tagName === 'IMG' ? e.target : null);
  };

  useEffect(() => {
    const onDocumentClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setSelectedImage(null);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  // A selected image that gets deleted must not leave the bar floating.
  useEffect(() => {
    if (selectedImage && !selectedImage.isConnected) setSelectedImage(null);
  });

  const styleImage = (patch) => {
    if (!selectedImage) return;
    applyImageStyle(selectedImage, patch);
    handleInput();
    positionSizeBar();
  };

  const currentWidth = parseInt(selectedImage?.style?.width, 10) || 100;

  const rawHeight = selectedImage?.style?.height;
  const currentHeight = rawHeight && rawHeight !== 'auto' ? parseInt(rawHeight, 10) : null;
  const currentFit = selectedImage?.style?.objectFit || 'cover';
  const currentRadius = parseInt(selectedImage?.style?.borderRadius, 10) || 0;

  // Width is a percentage, so it resolves against the containing block's
  // content box - parent padding has to come off or the drag drifts.
  const containerWidth = (img) => {
    const parent = img?.parentElement;
    if (!parent) return 0;
    const cs = window.getComputedStyle(parent);
    return parent.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
  };

  // Drag the corner handle to size the image by hand. Pointer capture keeps the
  // drag alive if the cursor leaves the handle, which it always does.
  const startResize = (e) => {
    const img = selectedImage;
    if (!img) return;
    e.preventDefault();
    e.stopPropagation();

    const available = containerWidth(img);
    if (!available) return;

    const rect = img.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPx: rect.width,
      startHeightPx: rect.height,
      available,
      // A fixed height makes the corner a true free transform; on auto height
      // the image keeps its ratio and only the width follows the drag.
      resizesHeight: currentHeight != null,
      // Which edge is grabbed is all that matters: pulling a right-side handle
      // right grows the image, pulling the left-side handle left does the same.
      // Alignment decides which edge stays put, not which way the drag reads.
      direction: e.currentTarget.dataset.dir === 'bl' ? -1 : 1,
    };

    // Synthetic pointers have no active capture; a failure here is harmless.
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (err) {
      /* no capture available - the move handler still tracks the pointer */
    }
  };

  const onResizeMove = (e) => {
    const drag = dragRef.current;
    if (!drag || !selectedImage) return;
    const deltaPx = (e.clientX - drag.startX) * drag.direction;
    const pct = ((drag.startPx + deltaPx) / drag.available) * 100;
    const clamped = Math.round(Math.min(100, Math.max(MIN_IMAGE_WIDTH, pct)));

    // Style the node directly during the drag; pushing every move through React
    // state would re-render the whole editor on each pointermove.
    selectedImage.style.width = `${clamped}%`;

    if (drag.resizesHeight) {
      const nextHeight = drag.startHeightPx + (e.clientY - drag.startY);
      selectedImage.style.height = `${Math.round(
        Math.min(IMAGE_HEIGHT.max, Math.max(IMAGE_HEIGHT.min, nextHeight))
      )}px`;
    }

    positionSizeBar();
  };

  const endResize = (e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    // The click that closes the drag lands on the editor, whose click handler
    // would read it as "clicked something that is not an image" and drop the
    // selection out from under the handles.
    justDraggedRef.current = true;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (err) {
      /* nothing was captured */
    }
    // Commit the final size to the form once, when the drag settles.
    handleInput();
    positionSizeBar();
  };

  // A float is a wrap; otherwise marginLeft:0 means it is pinned left,
  // marginRight:0 pinned right, and auto/auto is centred. The browser
  // normalises '0' to '0px'.
  const currentAlign = (() => {
    const float = selectedImage?.style?.float;
    if (float === 'left') return 'wrap-left';
    if (float === 'right') return 'wrap-right';
    const left = selectedImage?.style?.marginLeft;
    const right = selectedImage?.style?.marginRight;
    if (left === '0px') return 'left';
    if (right === '0px') return 'right';
    return 'center';
  })();

  useEffect(() => {
    if (editorRef.current && !isInternalChangeRef.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChangeRef.current = false;
  }, [value]);

  // Chrome's contentEditable makes a bare text line and then <div>s as the
  // author types. The article stylesheet only spaces <p>, so a typed article
  // read as one cramped block. Starting from a paragraph and making Enter
  // produce <p> keeps the markup the article page expects.
  const handleFocus = () => {
    document.execCommand('defaultParagraphSeparator', false, 'p');
    const editor = editorRef.current;
    if (editor && !editor.textContent.trim() && !editor.querySelector('img')) {
      const p = blankParagraph();
      editor.replaceChildren(p);
      placeCaretIn(p);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChangeRef.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const exec = (command, val = null) => {
    document.execCommand(command, false, val);
    if (editorRef.current) {
      editorRef.current.focus();
      handleInput();
    }
  };

  const formatBlock = (tag) => {
    document.execCommand('formatBlock', false, tag);
    if (editorRef.current) {
      editorRef.current.focus();
      handleInput();
    }
  };

  const insertLink = () => {
    const url = prompt('Enter hyperlink URL:');
    if (url) {
      exec('createLink', url);
    }
  };

  // Remember where the caret was while the author types, so an image picked
  // from a file dialog or the library - both of which take focus away - still
  // lands where they were writing rather than at the top of the article.
  useEffect(() => {
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      if (editorRef.current?.contains(range.startContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  // The direct child of the editor that holds node - the paragraph, heading or
  // list an image is moved around as a unit.
  const topBlock = (node) => {
    const editor = editorRef.current;
    if (!editor || !node || node === editor || !editor.contains(node)) return null;
    while (node.parentNode !== editor) node = node.parentNode;
    return node;
  };

  const isBlankBlock = (block) =>
    block.nodeType === Node.ELEMENT_NODE
      ? !block.textContent.trim() && !block.querySelector('img')
      : !block.textContent.trim();

  const blankParagraph = () => {
    const p = document.createElement('p');
    p.appendChild(document.createElement('br'));
    return p;
  };

  // The block an image sits in, on its own. An image dropped into the middle of
  // running text (older posts, pasted content) is lifted into a fresh paragraph
  // first, so moving it never drags the surrounding sentences along.
  const ownBlock = (img) => {
    const block = topBlock(img);
    if (!block) return null;
    const onlyImage =
      block.nodeType === Node.ELEMENT_NODE &&
      !block.textContent.trim() &&
      block.querySelectorAll('img').length === 1;
    if (onlyImage) return block;
    const p = document.createElement('p');
    block.after(p);
    p.appendChild(img);
    return p;
  };

  const placeCaretIn = (node) => {
    const range = document.createRange();
    range.selectNodeContents(node);
    range.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // Put an image in its own paragraph just after the one the caret is in, or in
  // place of an empty line. A blank paragraph follows it so there is always
  // somewhere to keep typing underneath.
  const insertImageBlock = (url, alt = '', { at } = {}) => {
    const editor = editorRef.current;
    if (!editor || !url) return;

    const img = document.createElement('img');
    img.src = url;
    img.alt = alt;
    applyImageStyle(img, { width: 100, align: 'center' });
    const p = document.createElement('p');
    p.appendChild(img);

    let anchor = at;
    if (anchor === undefined) {
      const range = savedRangeRef.current;
      if (range && editor.contains(range.startContainer)) {
        anchor =
          range.startContainer === editor
            ? editor.childNodes[Math.max(0, range.startOffset - 1)] || null
            : topBlock(range.startContainer);
      }
    }

    if (at && at.before && at.before.isConnected) {
      at.before.before(p);
    } else if (at && at.end) {
      editor.appendChild(p);
    } else if (anchor && anchor.parentNode === editor) {
      if (isBlankBlock(anchor)) anchor.replaceWith(p);
      else anchor.after(p);
    } else {
      editor.appendChild(p);
    }

    let next = p.nextSibling;
    if (!next || next.nodeType !== Node.ELEMENT_NODE || next.querySelector?.('img')) {
      next = blankParagraph();
      p.after(next);
    }

    editor.focus();
    placeCaretIn(next);
    handleInput();
    setSelectedImage(img);
    img.addEventListener(
      'load',
      () => {
        // A portrait photo at full width fills a whole screen of the article.
        // Start it at half width unless the author has already resized it.
        if (img.naturalHeight > img.naturalWidth && img.style.width === '100%') {
          applyImageStyle(img, { width: 50 });
          handleInput();
        }
        positionSizeBar();
      },
      { once: true }
    );
    img.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  useImperativeHandle(ref, () => ({ insertImage: (url, alt) => insertImageBlock(url, alt) }));

  const moveSelectedImage = (direction) => {
    const img = selectedImage;
    if (!img) return;
    const block = ownBlock(img);
    const sibling = direction < 0 ? block?.previousElementSibling : block?.nextElementSibling;
    if (!sibling) {
      toast.info(direction < 0 ? 'Image is already at the top' : 'Image is already at the bottom');
      return;
    }
    if (direction < 0) sibling.before(block);
    else sibling.after(block);
    handleInput();
    img.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    requestAnimationFrame(positionSizeBar);
  };

  const deleteSelectedImage = () => {
    const img = selectedImage;
    if (!img) return;
    const block = topBlock(img);
    img.remove();
    if (block && block.parentNode && isBlankBlock(block)) block.remove();
    setSelectedImage(null);
    handleInput();
  };

  // --- Drag and drop -------------------------------------------------------
  // Images are dragged between paragraphs, never into the middle of one, so a
  // drop always gives a clean layout. A line shows where it will land.

  const dropTarget = (clientY) => {
    const editor = editorRef.current;
    const blocks = Array.from(editor.children);
    for (const block of blocks) {
      const rect = block.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return { before: block, y: rect.top };
    }
    const last = blocks[blocks.length - 1];
    return { end: true, y: last ? last.getBoundingClientRect().bottom : editor.getBoundingClientRect().top + 20 };
  };

  const carriesImageFile = (e) =>
    Array.from(e.dataTransfer?.items || []).some(
      (item) => item.kind === 'file' && item.type.startsWith('image/')
    );

  const handleDragStart = (e) => {
    if (e.target.tagName !== 'IMG') return;
    draggedImageRef.current = e.target;
    e.dataTransfer.effectAllowed = 'move';
    // Firefox will not start a drag without some data attached.
    e.dataTransfer.setData('text/plain', '');
  };

  const handleDragOver = (e) => {
    if (!draggedImageRef.current && !carriesImageFile(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = draggedImageRef.current ? 'move' : 'copy';
    const wrapRect = wrapperRef.current.getBoundingClientRect();
    const editorRect = editorRef.current.getBoundingClientRect();
    const { y } = dropTarget(e.clientY);
    setDropMarker({
      top: Math.min(Math.max(y, editorRect.top), editorRect.bottom) - wrapRect.top,
      left: editorRect.left - wrapRect.left + 16,
      width: editorRect.width - 32,
    });
  };

  const clearDrag = () => {
    draggedImageRef.current = null;
    setDropMarker(null);
  };

  const handleDrop = (e) => {
    const img = draggedImageRef.current;
    const file = Array.from(e.dataTransfer?.files || []).find((f) => f.type.startsWith('image/'));
    if (!img && !file) return;
    // Our own drop handling replaces the browser's, which would paste a copy of
    // the image at the character under the pointer.
    e.preventDefault();
    const target = dropTarget(e.clientY);
    clearDrag();

    if (file) {
      uploadAndInsert(file, target);
      return;
    }

    const block = ownBlock(img);
    if (target.before === block) return;
    if (target.before) target.before.before(block);
    else editorRef.current.appendChild(block);
    handleInput();
    setSelectedImage(img);
    requestAnimationFrame(positionSizeBar);
  };

  const uploadAndInsert = async (file, at) => {
    if (!file.type.startsWith('image/')) {
      toast.error('File must be an image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploadingInline(true);
    try {
      const upload = await downscaleImage(file);

      const formData = new FormData();
      formData.append('file', upload);
      const res = await axios.post(`${API}/blog/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: false,
      });

      insertImageBlock(res.data.url, file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '), { at });
      toast.success(
        upload === file
          ? 'Image inserted into article'
          : `Image resized to ${formatBytes(upload.size)} and inserted`
      );
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingInline(false);
    }
  };

  const handleInlineImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) await uploadAndInsert(file);
  };

  return (
    <div ref={wrapperRef} className="relative border border-slate-200 rounded-2xl overflow-hidden bg-white">
      {/* Editor Toolbar */}
      <div className="bg-slate-50 border-b border-slate-200 p-2 flex flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={() => exec('bold')}
          className="p-2 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => exec('italic')}
          className="p-2 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => exec('underline')}
          className="p-2 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Underline"
        >
          <Underline className="w-4 h-4" />
        </button>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <button
          type="button"
          onClick={() => formatBlock('<h1>')}
          className="px-2.5 py-1 text-xs font-bold hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Heading 1"
        >
          H1
        </button>
        <button
          type="button"
          onClick={() => formatBlock('<h2>')}
          className="px-2.5 py-1 text-xs font-bold hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => formatBlock('<h3>')}
          className="px-2.5 py-1 text-xs font-bold hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Heading 3"
        >
          H3
        </button>
        <button
          type="button"
          onClick={() => formatBlock('<p>')}
          className="px-2.5 py-1 text-xs font-bold hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Paragraph"
        >
          ¶
        </button>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <button
          type="button"
          onClick={() => exec('insertUnorderedList')}
          className="p-2 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => exec('insertOrderedList')}
          className="p-2 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => formatBlock('<blockquote>')}
          className="p-2 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Quote Block"
        >
          <Quote className="w-4 h-4" />
        </button>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <button
          type="button"
          onClick={insertLink}
          className="p-2 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          title="Insert Link"
        >
          <LinkIcon className="w-4 h-4" />
        </button>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <label
          title="Upload a picture from your computer. It goes below the paragraph you were typing in."
          className="p-2 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1 text-xs font-semibold"
        >
          <Upload className="w-4 h-4 text-pink-600" />
          <span>{uploadingInline ? 'Uploading...' : 'Upload Image'}</span>
          <input
            ref={inlineImageInputRef}
            type="file"
            accept="image/*"
            onChange={handleInlineImageUpload}
            className="hidden"
            disabled={uploadingInline}
          />
        </label>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onOpenLibrary}
          title="Insert the cover, a curated photo or an image link"
          className="p-2 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors inline-flex items-center gap-1 text-xs font-semibold"
        >
          <Library className="w-4 h-4 text-pink-600" />
          <span>Image Library</span>
        </button>
        <span className="hidden lg:inline ml-auto pr-1 text-[11px] text-slate-400">
          Tip: drag images to move them, or drop a photo straight in
        </span>
      </div>

      {/* Editable HTML Area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onFocus={handleFocus}
        onClick={handleEditorClick}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={(e) => {
          if (!editorRef.current.contains(e.relatedTarget)) setDropMarker(null);
        }}
        onDrop={handleDrop}
        onDragEnd={clearDrag}
        className="p-5 min-h-[300px] max-h-[600px] overflow-y-auto focus:outline-none prose max-w-none text-slate-800 text-base [&_img]:cursor-grab"
        data-placeholder="Write your article content here..."
      />

      {/* Where a dragged image will land */}
      {dropMarker && (
        <div
          className="absolute z-30 pointer-events-none h-1 -mt-0.5 rounded-full bg-pink-500 shadow-[0_0_0_3px_rgba(236,72,153,0.2)]"
          style={{ top: dropMarker.top, left: dropMarker.left, width: dropMarker.width }}
        />
      )}

      {/* Outline + drag handles on the selected image */}
      {selectedImage && imageBox && (
        <div
          className="absolute z-10 pointer-events-none ring-2 ring-pink-500/70 rounded-lg"
          style={{
            top: imageBox.top,
            left: imageBox.left,
            width: imageBox.width,
            height: imageBox.height,
          }}
        >
          {[
            { id: 'br', cls: '-bottom-1.5 -right-1.5', cursor: 'nwse-resize' },
            { id: 'bl', cls: '-bottom-1.5 -left-1.5', cursor: 'nesw-resize' },
            { id: 'tr', cls: '-top-1.5 -right-1.5', cursor: 'nesw-resize' },
          ].map((handle) => (
            <div
              key={handle.id}
              data-dir={handle.id}
              role="slider"
              aria-label="Drag to resize image"
              aria-valuenow={currentWidth}
              aria-valuemin={MIN_IMAGE_WIDTH}
              aria-valuemax={100}
              tabIndex={-1}
              onPointerDown={startResize}
              onPointerMove={onResizeMove}
              onPointerUp={endResize}
              onPointerCancel={endResize}
              style={{ cursor: handle.cursor, touchAction: 'none' }}
              className={`pointer-events-auto absolute ${handle.cls} w-3 h-3 rounded-full bg-white border-2 border-pink-500 shadow-sm hover:scale-125 transition-transform`}
            />
          ))}
        </div>
      )}

      {/* Size, alignment, height, fit and radius for the selected image */}
      {selectedImage && sizeBarPos && (
        <div
          className="absolute z-20 flex flex-col gap-1 rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-1.5 py-1.5 shadow-lg max-w-[calc(100%-1rem)]"
          style={{ top: sizeBarPos.top, left: sizeBarPos.left }}
        >
          {/* Row 1 - width and alignment */}
          <div className="flex items-center gap-1">
            {IMAGE_SIZES.map((size) => (
              <button
                key={size.id}
                type="button"
                title={size.title}
                // preventDefault keeps the caret where it was; without it the
                // editor scrolls away as focus moves to the button.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => styleImage({ width: size.width })}
                className={`w-7 py-1 text-xs font-bold rounded-lg transition-colors ${
                  currentWidth === size.width
                    ? 'bg-pink-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {size.label}
              </button>
            ))}

            <div className="mx-0.5 h-5 w-px bg-slate-200" />

            {/* Free-form width. The slider stays interactive - the outside-click
                handler only deselects beyond the editor wrapper, so dragging
                here cannot drop the selection. */}
            <input
              type="range"
              min={MIN_IMAGE_WIDTH}
              max={100}
              value={currentWidth}
              onChange={(e) => styleImage({ width: Number(e.target.value) })}
              title="Custom width"
              className="w-20 accent-pink-600 cursor-pointer"
            />
            <span className="w-8 text-center text-[11px] font-semibold tabular-nums text-slate-500">
              {currentWidth}%
            </span>

            <div className="mx-0.5 h-5 w-px bg-slate-200" />

            {IMAGE_ALIGNMENTS.map(({ id, title, Icon }) => (
              <button
                key={id}
                type="button"
                title={title}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => styleImage({ align: id })}
                className={`p-1.5 rounded-lg transition-colors ${
                  currentAlign === id
                    ? 'bg-pink-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>

          {/* Row 2 - height, object-fit and corner radius */}
          <div className="flex items-center gap-1 border-t border-slate-100 pt-1">
            <button
              type="button"
              title="Toggle a fixed height on or off"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() =>
                styleImage({ height: currentHeight == null ? IMAGE_HEIGHT.default : 'auto' })
              }
              className={`px-2 py-1 text-[11px] font-bold rounded-lg transition-colors ${
                currentHeight == null
                  ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  : 'bg-pink-600 text-white'
              }`}
            >
              {currentHeight == null ? 'Auto H' : 'Fixed H'}
            </button>

            <input
              type="range"
              min={IMAGE_HEIGHT.min}
              max={IMAGE_HEIGHT.max}
              value={currentHeight ?? IMAGE_HEIGHT.default}
              disabled={currentHeight == null}
              onChange={(e) => styleImage({ height: Number(e.target.value) })}
              title={currentHeight == null ? 'Switch to a fixed height first' : 'Height'}
              className="w-20 accent-pink-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            />
            <span className="w-10 text-center text-[11px] font-semibold tabular-nums text-slate-500">
              {currentHeight == null ? 'auto' : `${currentHeight}px`}
            </span>

            <div className="mx-0.5 h-5 w-px bg-slate-200" />

            {/* object-fit only does anything once the box is no longer the
                image's own aspect ratio, so it follows the height toggle. */}
            {IMAGE_FITS.map((fit) => (
              <button
                key={fit.id}
                type="button"
                title={
                  currentHeight == null
                    ? 'Set a fixed height to use Cover or Fit'
                    : fit.title
                }
                disabled={currentHeight == null}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => styleImage({ fit: fit.id })}
                className={`px-2 py-1 text-[11px] font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  currentHeight != null && currentFit === fit.id
                    ? 'bg-pink-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100 disabled:hover:bg-transparent'
                }`}
              >
                {fit.label}
              </button>
            ))}

            <div className="mx-0.5 h-5 w-px bg-slate-200" />

            <span className="px-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Radius
            </span>
            {IMAGE_RADII.map(({ id, label, Icon, radius, title }) => (
              <button
                key={id}
                type="button"
                title={title}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => styleImage({ radius })}
                className={`w-7 py-1 rounded-lg transition-colors inline-flex items-center justify-center text-[11px] font-bold ${
                  currentRadius === radius
                    ? 'bg-pink-600 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {Icon ? <Icon className="w-3.5 h-3.5" /> : label}
              </button>
            ))}
          </div>

          {/* Row 3 - position in the article */}
          <div className="flex items-center gap-1 border-t border-slate-100 pt-1">
            <span className="px-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Move
            </span>
            <button
              type="button"
              title="Move above the previous paragraph"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => moveSelectedImage(-1)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-lg text-slate-600 hover:bg-slate-100"
            >
              <ArrowUp className="w-3.5 h-3.5" /> Up
            </button>
            <button
              type="button"
              title="Move below the next paragraph"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => moveSelectedImage(1)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-lg text-slate-600 hover:bg-slate-100"
            >
              <ArrowDown className="w-3.5 h-3.5" /> Down
            </button>
            <span className="px-1 text-[11px] text-slate-400">or drag it</span>

            <div className="mx-0.5 h-5 w-px bg-slate-200" />

            <button
              type="button"
              title="Remove this image from the article"
              onMouseDown={(e) => e.preventDefault()}
              onClick={deleteSelectedImage}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-lg text-rose-600 hover:bg-rose-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default function BlogManagerView() {
  const { token, API } = useAdmin();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importingFormat, setImportingFormat] = useState(null); // 'docx' | 'md'
  // Which image the library is picking: the cover, or one for the article body.
  const [libraryTarget, setLibraryTarget] = useState(null); // 'cover' | 'inline' | null
  const [libraryUrl, setLibraryUrl] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coverBroken, setCoverBroken] = useState(false);
  const editorApiRef = useRef(null);

  const [form, setForm] = useState(blankForm);

  useEffect(() => setCoverBroken(false), [form.image]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog/posts?include_drafts=true`, { withCredentials: false });
      setPosts(res.data.posts || []);
    } catch (err) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchPosts();
    }
  }, [token]);

  const generateSlug = (text) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const resetForm = () => {
    setForm(blankForm());
    setEditingPost(null);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      category: post.category || 'Relationship Tips',
      image: post.image || '',
      emoji: post.emoji || '💕',
      read_time: post.read_time || '5',
      published: post.published || false,
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      keywords: post.keywords || '',
    });
    setShowEditor(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await axios.delete(`${API}/blog/posts/${postId}`, { withCredentials: false });
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('File must be an image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const upload = await downscaleImage(file);

      const formData = new FormData();
      formData.append('file', upload);
      const postSlug = form.slug || generateSlug(form.title);
      const queryParams = new URLSearchParams();
      if (postSlug) queryParams.set('slug', postSlug);
      if (form.title) queryParams.set('title', form.title);

      const url = `${API}/blog/upload-image${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const res = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: false,
      });
      setForm((prev) => ({ ...prev, image: res.data.url }));
      toast.success(
        upload === file
          ? 'Cover image uploaded with SEO naming'
          : `Cover image resized to ${formatBytes(upload.size)} and uploaded`
      );
    } catch (err) {
      toast.error('Failed to upload cover image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const openLibrary = (target) => {
    setLibraryUrl('');
    setLibraryTarget(target);
  };

  const applyLibraryImage = (url, title = '') => {
    if (!url) return;
    if (libraryTarget === 'inline') {
      editorApiRef.current?.insertImage(url, title);
      toast.success('Image added to the article');
    } else {
      setForm((prev) => ({ ...prev, image: url }));
      toast.success('Cover image set');
    }
    setLibraryTarget(null);
  };

  const submitLibraryUrl = () => {
    const url = libraryUrl.trim();
    if (!/^https?:\/\/\S+$/i.test(url)) {
      toast.error('Paste a full image link starting with https://');
      return;
    }
    applyLibraryImage(url);
  };

  const handleDocumentImport = (format) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { extensions, label } = IMPORT_FORMATS[format];
    const name = file.name.toLowerCase();
    if (!extensions.some((ext) => name.endsWith(ext))) {
      toast.error(`Please select a ${label} file (${extensions.join(', ')})`);
      e.target.value = '';
      return;
    }

    setImportingFormat(format);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/blog/import-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: false,
      });

      const { title, content, excerpt } = res.data;
      setForm((prev) => ({
        ...prev,
        title: title || prev.title,
        slug: generateSlug(title || prev.title),
        content: content || prev.content,
        excerpt: excerpt || prev.excerpt,
      }));
      toast.success(`${label} imported into editor!`);
    } catch (err) {
      // Without a response the request never completed (offline, CORS, or the
      // API is down), so say that rather than blaming the file.
      toast.error(
        err.response?.data?.detail ||
          (err.response
            ? `Failed to import this ${label} file`
            : 'Could not reach the server. Check your connection and try again.')
      );
    } finally {
      setImportingFormat(null);
      e.target.value = '';
    }
  };

  // publish decides the article's state explicitly on every save, so the
  // button the author presses is the state they get: "Publish" puts it live,
  // "Save Draft" / "Unpublish" keeps it off the public blog.
  const savePost = async (publish) => {
    if (!form.title.trim()) {
      toast.error('Add a title before saving');
      return;
    }
    if (!hasArticleBody(form.content)) {
      toast.error('The article is empty - write something before saving');
      return;
    }

    const cleanSlug = generateSlug(form.slug || form.title);
    if (!cleanSlug) {
      toast.error('A valid URL slug is required');
      return;
    }

    if (RESERVED_SLUGS.has(cleanSlug)) {
      toast.error(`'${cleanSlug}' is a reserved system URL. Please pick a different slug.`);
      return;
    }

    const payload = {
      ...form,
      content: stripRepeatedTitle(form.content, form.title),
      published: publish,
      slug: cleanSlug,
      meta_title: form.meta_title?.trim() || form.title.trim(),
      meta_description: form.meta_description?.trim() || form.excerpt?.trim(),
      keywords: form.keywords?.trim() || '',
    };

    setSaving(true);
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/posts/${editingPost.id}`, payload, { withCredentials: false });
      } else {
        await axios.post(`${API}/blog/posts`, payload, { withCredentials: false });
      }

      if (publish) {
        toast.success(editingPost?.published ? 'Live article updated' : 'Article published!', {
          description: `Live at coupleinapp.com/${cleanSlug}`,
          action: { label: 'View live', onClick: () => window.open(`/${cleanSlug}`, '_blank', 'noopener') },
        });
      } else {
        toast.success(
          editingPost?.published ? 'Article unpublished and saved as a draft' : 'Draft saved',
          { description: 'Drafts are not visible on the public blog.' }
        );
      }
      setShowEditor(false);
      resetForm();
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const filteredPosts = posts.filter((p) => {
    const q = search.toLowerCase().trim();
    const matchesSearch =
      !q ||
      (p.title || '').toLowerCase().includes(q) ||
      (p.excerpt || '').toLowerCase().includes(q) ||
      (p.keywords || '').toLowerCase().includes(q) ||
      (p.slug || '').toLowerCase().includes(q);
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <AdminLayout activeTab="blog">
      {/* Editor Modal / View */}
      {showEditor ? (
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-8 shadow-sm space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-5">
            <button
              type="button"
              onClick={() => {
                setShowEditor(false);
                resetForm();
              }}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Articles</span>
            </button>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {Object.entries(IMPORT_FORMATS).map(([format, config]) => {
                const busy = importingFormat === format;
                return (
                  <label
                    key={format}
                    title={`Import a ${config.label} (${config.extensions.join(', ')})`}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 bg-pink-50 text-pink-700 text-xs font-semibold rounded-xl transition-colors ${
                      importingFormat
                        ? 'opacity-60 cursor-not-allowed'
                        : 'hover:bg-pink-100 cursor-pointer'
                    }`}
                  >
                    {format === 'md' ? (
                      <FileCode className="w-4 h-4" />
                    ) : (
                      <FileUp className="w-4 h-4" />
                    )}
                    <span>{busy ? 'Importing...' : config.button}</span>
                    <input
                      type="file"
                      accept={config.accept}
                      onChange={handleDocumentImport(format)}
                      className="hidden"
                      disabled={Boolean(importingFormat)}
                    />
                  </label>
                );
              })}

              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPreview(true)}
                className="rounded-xl"
              >
                <Eye className="w-4 h-4 mr-1.5" />
                Preview
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={saving}
                onClick={() => savePost(false)}
                className="rounded-xl"
                title={editingPost?.published ? 'Take it off the public blog and keep it as a draft' : 'Save without publishing'}
              >
                {editingPost?.published ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                {editingPost?.published ? 'Unpublish' : 'Save Draft'}
              </Button>
              <Button
                type="button"
                disabled={saving}
                onClick={() => savePost(true)}
                className="bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-sm"
              >
                <Send className="w-4 h-4 mr-1.5" />
                {saving ? 'Saving...' : editingPost?.published ? 'Update Live Article' : 'Publish'}
              </Button>
            </div>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Article Title *
                </label>
                <Input
                  value={form.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      title,
                      slug: editingPost ? prev.slug : generateSlug(title),
                    }));
                  }}
                  placeholder="e.g. 5 Simple Ways to Reconnect Every Evening"
                  className="text-base font-semibold"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  URL Slug (Live at https://coupleinapp.com/:slug) *
                </label>
                <div className="relative flex items-center">
                  <span className="inline-flex items-center px-3 py-2 text-xs font-mono text-slate-400 bg-slate-100 border border-r-0 border-slate-200 rounded-l-xl select-none">
                    /
                  </span>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                    placeholder="5-simple-ways-to-reconnect"
                    className="font-mono text-sm rounded-l-none"
                    required
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1 font-mono">
                  Live URL: https://coupleinapp.com/{form.slug || 'slug'}
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full h-10 px-3 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:ring-2 focus:ring-pink-500"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Read Time (Minutes)
                </label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={form.read_time}
                  onChange={(e) => setForm((prev) => ({ ...prev, read_time: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Emoji Icon
                </label>
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, emoji }))}
                      className={`text-xl p-1.5 rounded-lg transition-all ${
                        form.emoji === emoji ? 'bg-pink-100 scale-110 shadow-xs' : 'hover:bg-slate-100'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Cover Image (Optional)
              </label>
              <p className="text-xs text-slate-500 -mt-1 mb-3">
                Shown at the top of the article, on the blog list and when the link is shared.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                {/* Shown the way the article page frames it: 16:9, cropped to fill */}
                <div className="relative w-full sm:w-80 aspect-video flex-shrink-0 rounded-2xl overflow-hidden border border-slate-200 bg-gradient-to-br from-pink-50 to-orange-50">
                  {form.image && !coverBroken ? (
                    <img
                      src={form.image}
                      alt="Cover preview"
                      onError={() => setCoverBroken(true)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-center px-4">
                      {form.image ? (
                        <>
                          <X className="w-6 h-6 text-rose-500" />
                          <p className="text-xs font-semibold text-rose-600">This image could not be loaded</p>
                          <p className="text-[11px] text-slate-500">Check the link or pick another picture.</p>
                        </>
                      ) : (
                        <>
                          <ImageIcon className="w-7 h-7 text-pink-400" />
                          <p className="text-xs font-semibold text-slate-600">No cover yet</p>
                          <p className="text-[11px] text-slate-400">Upload one or choose from the library</p>
                        </>
                      )}
                    </div>
                  )}
                  {uploading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-xs font-semibold text-slate-700">
                      Uploading...
                    </div>
                  )}
                </div>

                <div className="flex flex-row flex-wrap sm:flex-col gap-2">
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl cursor-pointer transition-colors">
                    <Upload className="w-4 h-4 text-pink-600" />
                    <span>{uploading ? 'Uploading...' : form.image ? 'Upload a Different Image' : 'Upload Image'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCoverUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => openLibrary('cover')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-pink-50 hover:bg-pink-100 text-pink-700 text-xs font-semibold rounded-xl transition-colors"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Choose from Library</span>
                  </button>
                  {form.image && !coverBroken && (
                    <button
                      type="button"
                      onClick={() => editorApiRef.current?.insertImage(form.image, form.title)}
                      title="Also place this picture inside the article text, where your cursor was"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition-colors"
                    >
                      <Plus className="w-4 h-4 text-pink-600" />
                      <span>Insert into Article Text</span>
                    </button>
                  )}
                  {form.image && (
                    <button
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, image: '' }))}
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Remove Cover</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Short Excerpt
              </label>
              <textarea
                value={form.excerpt}
                onChange={(e) => setForm((prev) => ({ ...prev, excerpt: e.target.value }))}
                placeholder="A brief 1-2 sentence preview for search engines and social cards..."
                rows={2}
                className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:ring-2 focus:ring-pink-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Article Content *
              </label>
              <RichTextEditor
                ref={editorApiRef}
                value={form.content}
                onChange={(content) => setForm((prev) => ({ ...prev, content }))}
                onOpenLibrary={() => openLibrary('inline')}
              />
            </div>

            {/* Dedicated SEO & Google Search Snippet Preview Section */}
            <div className="bg-slate-50/80 rounded-2xl border border-slate-200/80 p-5 sm:p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SearchCheck className="w-5 h-5 text-pink-600" />
                  <h3 className="text-sm font-bold text-slate-900">SEO & Search Engine Snippet</h3>
                </div>
                <span className="text-xs bg-pink-100 text-pink-700 font-semibold px-2.5 py-0.5 rounded-full">
                  100% SEO Ready
                </span>
              </div>

              {/* Google SERP Preview Card */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-1">
                <p className="text-[11px] uppercase font-bold text-slate-400 tracking-wider">
                  Google Search Snippet Preview
                </p>
                <div className="flex items-center gap-2 pt-1">
                  <div className="w-4 h-4 rounded-full bg-pink-500 flex items-center justify-center text-[9px] text-white font-bold">
                    C
                  </div>
                  <span className="text-xs text-slate-600 font-mono truncate">
                    https://coupleinapp.com/{form.slug || generateSlug(form.title) || 'post-slug'}
                  </span>
                </div>
                <h4 className="text-base text-blue-700 hover:underline font-medium cursor-pointer line-clamp-1">
                  {form.meta_title || form.title || 'Your Post Title Here'} | CoupleIn
                </h4>
                <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                  {form.meta_description || form.excerpt || 'Enter a meta description to control how this article appears in search results and social cards.'}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4 pt-2">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      SEO Meta Title
                    </label>
                    <span className={`text-[11px] font-mono ${(form.meta_title || form.title).length > 60 ? 'text-amber-600 font-bold' : 'text-slate-400'}`}>
                      {(form.meta_title || form.title).length}/60 chars
                    </span>
                  </div>
                  <Input
                    value={form.meta_title}
                    onChange={(e) => setForm((prev) => ({ ...prev, meta_title: e.target.value }))}
                    placeholder={form.title || 'Leave blank to use article title'}
                    className="text-xs sm:text-sm bg-white"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">Recommended: 50–60 characters</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      SEO Meta Description
                    </label>
                    <span className={`text-[11px] font-mono ${(form.meta_description || form.excerpt).length > 160 ? 'text-amber-600 font-bold' : 'text-slate-400'}`}>
                      {(form.meta_description || form.excerpt).length}/160 chars
                    </span>
                  </div>
                  <textarea
                    value={form.meta_description}
                    onChange={(e) => setForm((prev) => ({ ...prev, meta_description: e.target.value }))}
                    placeholder={form.excerpt || 'Leave blank to use excerpt'}
                    rows={2}
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:ring-2 focus:ring-pink-500"
                  />
                  <p className="text-[11px] text-slate-400 mt-0.5">Recommended: 140–160 characters</p>
                </div>
              </div>

              {/* SEO Target Keywords & Search Query Matching */}
              <div className="pt-2 border-t border-slate-200/60">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    SEO Keywords & Search Tags (Comma Separated)
                  </label>
                  <span className="text-[11px] text-slate-400">
                    Matches search queries on the blog & search engines
                  </span>
                </div>
                <Input
                  value={form.keywords}
                  onChange={(e) => setForm((prev) => ({ ...prev, keywords: e.target.value }))}
                  placeholder="e.g. couples communication, fight resolution, love languages, relationship advice"
                  className="text-xs sm:text-sm bg-white"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  When visitors search for these terms on your blog or search engines, this article will match directly.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    editingPost?.published
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}
                >
                  {editingPost?.published ? 'Live' : editingPost ? 'Draft' : 'Not saved yet'}
                </span>
                <p className="text-xs text-slate-500">
                  {editingPost?.published
                    ? 'This article is on the public blog. Updates go live as soon as you save.'
                    : 'Publish puts it on the public blog. Save Draft keeps it private.'}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowEditor(false);
                    resetForm();
                  }}
                  className="rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                type="button"
                variant="outline"
                onClick={() => setShowPreview(true)}
                className="rounded-xl"
              >
                <Eye className="w-4 h-4 mr-1.5" />
                Preview
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={saving}
                onClick={() => savePost(false)}
                className="rounded-xl"
                title={editingPost?.published ? 'Take it off the public blog and keep it as a draft' : 'Save without publishing'}
              >
                {editingPost?.published ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                {editingPost?.published ? 'Unpublish' : 'Save Draft'}
              </Button>
              <Button
                type="button"
                disabled={saving}
                onClick={() => savePost(true)}
                className="bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-sm"
              >
                <Send className="w-4 h-4 mr-1.5" />
                {saving ? 'Saving...' : editingPost?.published ? 'Update Live Article' : 'Publish'}
              </Button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        /* Blog Post List View */
        <div className="space-y-6">
          {/* Controls Bar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-xs">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                Relationship Blog Articles
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                Manage, edit, publish, and import Word .docx guides for CoupleIn readers.
              </p>
            </div>

            <Button
              onClick={() => {
                resetForm();
                setShowEditor(true);
              }}
              className="bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow-md shadow-pink-500/20 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span>Create New Post</span>
            </Button>
          </div>

          {/* Search & Category Filter */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search articles by title..."
                className="pl-10 bg-white"
              />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-1">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  categoryFilter === 'all'
                    ? 'bg-pink-600 text-white shadow-xs'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                All Categories
              </button>
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                    categoryFilter === cat
                      ? 'bg-pink-600 text-white shadow-xs'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Post Table / Cards */}
          {loading ? (
            <div className="bg-white rounded-3xl p-12 text-center text-slate-400 border border-slate-200">
              <div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm font-medium">Loading articles...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center text-slate-400 border border-slate-200 space-y-3">
              <div className="w-12 h-12 bg-pink-50 text-pink-500 rounded-2xl flex items-center justify-center mx-auto text-2xl">
                ✍️
              </div>
              <h3 className="text-base font-bold text-slate-800">No articles found</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                {search ? 'Try adjusting your search query' : 'Get started by creating your first post or importing a Word document.'}
              </p>
              <Button
                onClick={() => {
                  resetForm();
                  setShowEditor(true);
                }}
                variant="outline"
                className="mt-2 text-xs"
              >
                Create Article
              </Button>
            </div>
          ) : (
            <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs overflow-hidden divide-y divide-slate-100">
              {filteredPosts.map((post) => (
                <div
                  key={post.id || post.slug}
                  className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50/60 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-50 to-orange-50 border border-pink-100 flex-shrink-0 overflow-hidden flex items-center justify-center text-2xl">
                      {post.image ? (
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                      ) : (
                        post.emoji || '💕'
                      )}
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {post.category && (
                          <span className="text-[11px] font-bold bg-pink-50 text-pink-600 px-2 py-0.5 rounded-md border border-pink-100">
                            {post.category}
                          </span>
                        )}
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            post.published
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </div>

                      <h3 className="text-base font-bold text-slate-900 hover:text-pink-600 transition-colors line-clamp-1">
                        {post.title}
                      </h3>

                      <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                        <span className="flex items-center gap-1 font-mono">
                          /{post.slug}
                        </span>
                        <span>•</span>
                        <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{post.read_time || 5} min read</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-end sm:self-center">
                    {post.published && (
                      <a
                        href={`/${post.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-400 hover:text-pink-600 hover:bg-slate-100 rounded-xl transition-colors"
                        title="View Live Article"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}

                    <button
                      onClick={() => handleEdit(post)}
                      className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors font-semibold text-xs inline-flex items-center gap-1"
                    >
                      <Edit2 className="w-4 h-4" />
                      <span>Edit</span>
                    </button>

                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-xl transition-colors"
                      title="Delete Article"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Image library - picks the cover or an image for the article body */}
      {libraryTarget && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onMouseDown={(e) => e.target === e.currentTarget && setLibraryTarget(null)}
        >
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden border border-slate-100 animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-pink-600" />
                  <span>{libraryTarget === 'cover' ? 'Choose a Cover Image' : 'Add an Image to the Article'}</span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {libraryTarget === 'cover'
                    ? 'Click a picture to make it the cover.'
                    : 'Click a picture to place it below the paragraph your cursor was in.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLibraryTarget(null)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pt-4 flex flex-col sm:flex-row gap-2">
              <Input
                value={libraryUrl}
                onChange={(e) => setLibraryUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), submitLibraryUrl())}
                placeholder="Or paste an image link (https://...)"
                className="text-sm"
              />
              <Button type="button" variant="outline" onClick={submitLibraryUrl} className="rounded-xl">
                <LinkIcon className="w-4 h-4 mr-1.5" />
                Use Link
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 grid auto-rows-max grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {libraryTarget === 'inline' && form.image && (
                <button
                  type="button"
                  onClick={() => applyLibraryImage(form.image, form.title)}
                  className="group text-left rounded-2xl overflow-hidden border-2 border-pink-300 hover:border-pink-500 hover:shadow-lg transition-all flex flex-col bg-pink-50"
                >
                  <div className="relative aspect-[4/3] shrink-0 overflow-hidden bg-slate-100">
                    <img src={form.image} alt="This article's cover" className="w-full h-full object-cover" />
                    <span className="absolute bottom-2 left-2 bg-pink-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded-md">
                      This article
                    </span>
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-semibold text-pink-700 line-clamp-1">Your cover image</p>
                  </div>
                </button>
              )}
              {PLACEHOLDER_IMAGES.map((img) => (
                <button
                  type="button"
                  key={img.id}
                  onClick={() => applyLibraryImage(img.url, img.title)}
                  className="group text-left rounded-2xl overflow-hidden border border-slate-200 hover:border-pink-500 hover:shadow-lg transition-all flex flex-col bg-slate-50"
                >
                  <div className="relative aspect-[4/3] shrink-0 overflow-hidden bg-slate-100">
                    <img
                      src={img.url.replace('w=1200', 'w=400')}
                      alt={img.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <span className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-md">
                      {img.category}
                    </span>
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-semibold text-slate-800 line-clamp-1 group-hover:text-pink-600 transition-colors">
                      {img.title}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setLibraryTarget(null)}
                className="rounded-xl"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Article preview - the same layout and typography as the live page */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          onMouseDown={(e) => e.target === e.currentTarget && setShowPreview(false)}
        >
          <div className="bg-gray-50 rounded-3xl max-w-4xl w-full my-8 shadow-2xl overflow-hidden">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-6 py-3 bg-white border-b border-slate-200">
              <p className="text-xs font-semibold text-slate-500">
                Preview - how readers will see coupleinapp.com/{generateSlug(form.slug || form.title) || 'slug'}
              </p>
              <Button type="button" variant="outline" onClick={() => setShowPreview(false)} className="rounded-xl">
                <X className="w-4 h-4 mr-1.5" />
                Close Preview
              </Button>
            </div>
            <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
              {form.category && (
                <span className="inline-block bg-pink-100 text-pink-600 text-sm font-medium px-4 py-1 rounded-full mb-4">
                  {form.category}
                </span>
              )}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {form.title || 'Untitled article'}
              </h1>
              <div className="flex items-center gap-6 text-gray-500 mb-8">
                <span className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {new Date(editingPost?.created_at || Date.now()).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {form.read_time || '5'} min read
                </span>
              </div>
              {form.image && (
                <div className="mb-8 rounded-2xl overflow-hidden">
                  <img src={form.image} alt={form.title} className="w-full aspect-video object-cover" />
                </div>
              )}
              {hasArticleBody(form.content) ? (
                <div
                  className={ARTICLE_PROSE_CLASSES}
                  dangerouslySetInnerHTML={{ __html: stripRepeatedTitle(form.content, form.title) }}
                />
              ) : (
                <p className="text-gray-400 italic">The article body is empty.</p>
              )}
            </article>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
